import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ColumnDef } from '@tanstack/react-table';
import { WhitelistWallet, getWhitelists, deleteWhitelistWallet } from '../utils/whitelistService';
import { extractErrorMessage } from '../utils/errorHandling';
import { switchToken } from '../functions/SwitchToken';
import Table from './common/Table';
import StatusBadge from './common/StatusBadge';
import WalletAddress from './common/WalletAddress';
import TimezoneAwareTimestamp from './common/TimezoneAwareTimestamp';
import Button from './common/Button';
import { IcnClose } from '../assets/icons';
import styles from '../styles/WhitelistTable.module.scss';

interface WhitelistTableProps {
  refreshTrigger?: number;
}

// Helper function to derive status from is_compliant field
const getWalletStatus = (isCompliant: boolean): 'pending' | 'compliant' | 'rejected' => {
  // For now, we'll map is_compliant directly. In future, backend can provide proper status
  return isCompliant ? 'compliant' : 'pending';
};

function WhitelistTable({ refreshTrigger }: WhitelistTableProps) {
  const [whitelists, setWhitelists] = useState<WhitelistWallet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadWhitelists = async () => {
    try {
      setIsLoading(true);
      const data = await getWhitelists();
      // Add derived status to each wallet
      const walletsWithStatus = data.map(wallet => ({
        ...wallet,
        status: getWalletStatus(wallet.is_compliant)
      }));
      setWhitelists(walletsWithStatus);
    } catch (error: any) {
      console.error("Error loading whitelists:", error);
      const errorMessage = extractErrorMessage(error, "Failed to load whitelists");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWhitelists();
  }, [refreshTrigger]);

  const handleDelete = useCallback(async (walletId: number) => {
    const confirmDelete = () => {
      performDelete(walletId);
    };

    toast.warn(
      ({ closeToast }: { closeToast?: () => void }) => (
        <div>
          <p>Are you sure you want to remove this wallet from the whitelist?</p>
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button 
              onClick={() => closeToast && closeToast()}
              style={{ padding: '4px 8px', border: '1px solid #ccc', background: 'white', borderRadius: '4px', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button 
              onClick={() => {
                confirmDelete();
                closeToast && closeToast();
              }}
              style={{ padding: '4px 8px', border: 'none', background: '#ff4757', color: 'white', borderRadius: '4px', cursor: 'pointer' }}
            >
              Delete
            </button>
          </div>
        </div>
      ),
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
      }
    );
  }, []);

  const performDelete = async (walletId: number) => {
    try {
      setDeletingId(walletId);
      await deleteWhitelistWallet(walletId);
      // Remove from local state
      setWhitelists(prev => prev.filter(wallet => wallet.id !== walletId));
    } catch (error: any) {
      console.error("Error deleting wallet:", error);
      const errorMessage = extractErrorMessage(error, "Failed to delete wallet from whitelist");
      toast.error(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };



  const formatChainName = (chain: string) => {
    return chain.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Define table columns using TanStack Table
  const columns = useMemo<ColumnDef<WhitelistWallet>[]>(
    () => [
      {
        id: 'label',
        header: 'Label',
        accessorKey: 'label',
        cell: ({ getValue }) => {
          const label = getValue() as string;
          return (
            <div className={styles.labelCell}>
              <span className={styles.labelText} title={label}>
                {label}
              </span>
            </div>
          );
        },
      },
      {
        id: 'wallet_address',
        header: 'Wallet Address',
        accessorKey: 'wallet_address',
        cell: ({ getValue }) => {
          const address = getValue() as string;
          return (
            <WalletAddress 
              address={address}
              showCopyButton={true}
              truncateLength={{ start: 6, end: 4 }}
            />
          );
        },
      },
      {
        id: 'token',
        header: 'Token',
        accessorKey: 'token',
        cell: ({ getValue }) => {
          const token = getValue() as string;
          return (
            <div className={styles.tokenCell}>
              <span className={styles.tokenBadge}>
                {switchToken(token, 'image')}
                <span>{token}</span>
              </span>
            </div>
          );
        },
      },
      {
        id: 'chain',
        header: 'Chain',
        accessorKey: 'chain',
        cell: ({ getValue }) => {
          const chain = getValue() as string;
          return (
            <span className={styles.chainText}>
              {formatChainName(chain)}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue() as 'pending' | 'compliant' | 'rejected';
          return <StatusBadge status={status} size="sm" />;
        },
      },
      {
        id: 'dt_created',
        header: 'Date Added',
        accessorKey: 'dt_created',
        cell: ({ getValue }) => {
          const date = getValue() as string;
          return (
            <TimezoneAwareTimestamp 
              dateString={date} 
              format="datetime"
              className={styles.dateText}
            />
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const wallet = row.original;
          return (
            <div className={styles.actionsCell}>
              <Button
                btnVariant="icon"
                btnColor="grey"
                icon={<IcnClose />}
                onClick={() => handleDelete(wallet.id)}
                disabled={deletingId === wallet.id}
                title="Remove from whitelist"
                extraClassName={styles.deleteButton}
              />
            </div>
          );
        },
      },
    ],
    [deletingId, handleDelete]
  );

  return (
    <div className={styles.whitelistTable}>
      <Table
        data={whitelists}
        columns={columns}
        isLoading={isLoading}
        customDefaultEmpty={true}
        defaultEmptyHeading="No whitelisted wallets found"
        defaultEmptyTitle="Add a wallet to get started"
        pageSize={10}
        extraClassName={styles.tableContainer}
      />
    </div>
  );
}

export default WhitelistTable;
